"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Shield } from "lucide-react";

export default function ZeroFeesLegalScroll() {
  const { t } = useLang();

  const steps = [
    {
      title: t("The menu shows the card price", "El menú muestra el precio con tarjeta"),
      body: t(
        "Your listed prices are the card prices, designed to cover the processing cost.",
        "Tus precios publicados son los precios con tarjeta, pensados para cubrir el costo de procesamiento."
      ),
    },
    {
      title: t("Cash gets a discount", "El efectivo recibe un descuento"),
      body: t(
        "4% by default, shown as its own \"Cash discount\" line on the receipt.",
        "4% por defecto, que aparece como su propia línea de \"Descuento en efectivo\" en el recibo."
      ),
    },
    {
      title: t("Your signage, your responsibility", "Tus avisos, tu responsabilidad"),
      body: t(
        "Rules vary by state and card network. You're responsible for the signage and disclosures your state requires — ask us or your payment processor if you're unsure.",
        "Las reglas cambian según el estado y la red de tarjetas. Tú eres responsable de los avisos que exige tu estado — pregúntanos a nosotros o a tu procesador de pagos si tienes dudas."
      ),
    },
  ];

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#00A876" }}>
                {t("How it works", "Cómo funciona")}
              </div>
              <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal mb-6">
                {t("Simple model.", "Modelo simple.")}
                <br />
                <span className="text-redy-charcoal/30">{t("Check your state.", "Revisa tu estado.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mb-6">
                {t(
                  "Redy uses the cash-discount model — the same idea many gas stations use. Redy shows the cash discount at checkout and on the receipt. Signage and disclosure rules vary by state and card network, and meeting them is up to you. Not sure what your state requires? Ask us or your payment processor.",
                  "Redy usa el modelo de descuento en efectivo — la misma idea que usan muchas gasolineras. Redy muestra el descuento en efectivo al cobrar y en el recibo. Las reglas de avisos cambian según el estado y la red de tarjetas, y cumplirlas te corresponde a ti. ¿No sabes qué exige tu estado? Pregúntanos a nosotros o a tu procesador de pagos."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white px-4 py-3 rounded-full border border-black/5 shadow-sm">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,200,150,0.15)" }}>
                  <Shield className="w-3.5 h-3.5" style={{ color: "#00A876" }} />
                </div>
                <div className="text-[13px] font-bold text-redy-charcoal">
                  {t("Cash discount shown at checkout and on the receipt", "Descuento en efectivo visible al cobrar y en el recibo")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: how dual pricing works */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="relative bg-white rounded-[28px] border border-black/5 shadow-[0_20px_60px_rgba(0,0,0,0.04)] p-8 md:p-10"
            >
              {/* Header */}
              <div className="flex items-baseline justify-between mb-6">
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                  {t("Dual pricing, step by step", "Dual pricing, paso a paso")}
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {steps.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{
                      duration: 0.4,
                      delay: 0.1 * i,
                      ease: [0.21, 0.47, 0.32, 0.98],
                    }}
                    className="flex items-start gap-4 rounded-2xl bg-[#FAFAF8] border border-black/5 p-5"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[12px] font-black text-white"
                      style={{ backgroundColor: "#00C896" }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <div className="text-[15px] font-black text-redy-charcoal mb-1">{step.title}</div>
                      <p className="text-[13px] font-medium text-redy-secondary leading-relaxed">{step.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Caption */}
              <p className="text-[11px] font-medium text-redy-secondary/60 mt-6 text-center">
                {t(
                  "Your default rate is set during payments onboarding; you can see it any time in Settings → Payments.",
                  "Tu tasa por defecto se define al configurar tu cuenta de pagos; la ves cuando quieras en Configuración → Pagos."
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
